/**
 * TOPIK 考试特定的题目类型定义（按 TOPIK II 结构）
 */

import {
  QuestionType,
  createExamQuestionTypeDefinition,
  createFieldDefinition,
  type ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';

// TOPIK I/II 等级 1–6（产线 Question.level 与专属 topikLevel 同源）
const TOPIK_LEVELS = ['1', '2', '3', '4', '5', '6'];

// TOPIK 等级字段（听力/阅读/写作共用）
const topikLevelField = createFieldDefinition(
  'topikLevel',
  'string',
  true,
  'TOPIK 等级 (1-6)',
  {
    validator: (value) => {
      return TOPIK_LEVELS.includes(value as string)
        ? true
        : `TOPIK 等级必须是以下之一: ${TOPIK_LEVELS.join(', ')}`;
    },
  }
);

/**
 * TOPIK 听力题定义
 */
export const topikListeningDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'topik_listening',
    QuestionType.SINGLE_CHOICE,
    'TOPIK 听力题',
    {
      requiredStimulus: ['audio'],
      extraFields: {
        // TOPIK II 等级
        topikLevel: topikLevelField,
        // 题号（TOPIK II 听力固定 50 题）
        questionNumber: createFieldDefinition(
          'questionNumber',
          'number',
          false,
          '题号 (1-50)',
          {
            validator: (value) => {
              const num = Number(value);
              if (num < 1 || num > 50) {
                return 'TOPIK 听力题号必须在 1-50 之间';
              }
              return true;
            },
          }
        ),
      },

      constraints: {
        // TOPIK 听力题为 4 选 1
        expectedOptionCount: 4,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证题号范围（JSON 中可能存成字符串，统一转成 number 再比较）
        const questionNumber = Number(question.questionNumber);
        if (
          question.questionNumber != null &&
          (Number.isNaN(questionNumber) || questionNumber < 1 || questionNumber > 50)
        ) {
          errors.push({
            field: 'questionNumber',
            message: 'TOPIK 听力题号必须在 1-50 之间',
            code: 'TOPIK_QUESTION_NUMBER_OUT_OF_RANGE',
          });
        }

        // 验证选项数量
        if (question.options && question.options.length !== 4) {
          errors.push({
            field: 'options',
            message: 'TOPIK 听力题通常为 4 个选项',
            code: 'TOPIK_OPTIONS_COUNT',
          });
        }

        return errors;
      },
    }
  );

/**
 * TOPIK 阅读题定义
 */
export const topikReadingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'topik_reading',
    QuestionType.SINGLE_CHOICE,
    'TOPIK 阅读理解题',
    {
      requiredStimulus: ['passage'],
      extraFields: {
        // TOPIK II 等级
        topikLevel: topikLevelField,
        // 题号（TOPIK II 阅读固定 50 题）
        questionNumber: createFieldDefinition(
          'questionNumber',
          'number',
          false,
          '题号 (1-50)',
          {
            validator: (value) => {
              const num = Number(value);
              if (num < 1 || num > 50) {
                return 'TOPIK 阅读题号必须在 1-50 之间';
              }
              return true;
            },
          }
        ),
      },

      constraints: {
        // TOPIK 阅读题为 4 选 1
        expectedOptionCount: 4,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证题号范围
        const questionNumber = Number(question.questionNumber);
        if (
          question.questionNumber != null &&
          (Number.isNaN(questionNumber) || questionNumber < 1 || questionNumber > 50)
        ) {
          errors.push({
            field: 'questionNumber',
            message: 'TOPIK 阅读题号必须在 1-50 之间',
            code: 'TOPIK_QUESTION_NUMBER_OUT_OF_RANGE',
          });
        }

        // 验证选项数量
        if (question.options && question.options.length !== 4) {
          errors.push({
            field: 'options',
            message: 'TOPIK 阅读题通常为 4 个选项',
            code: 'TOPIK_OPTIONS_COUNT',
          });
        }

        return errors;
      },
    }
  );

/**
 * TOPIK 写作题定义（仅 TOPIK II，共 4 题：51-54 题）
 */
export const topikWritingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'topik_writing',
    QuestionType.FILL_BLANK, // 写作为开放式作答，用填空类型承载
    'TOPIK 写作题',
    {
      responseMode: 'open_ended',
      extraFields: {
        // TOPIK II 等级
        topikLevel: topikLevelField,
        // 写作任务编号（51-52 为完成句子/短文写作，53 为图表说明文，54 为议论文）
        task: createFieldDefinition('task', 'number', true, '写作任务编号 (1-4)', {
          validator: (value) => {
            const num = Number(value);
            if (num < 1 || num > 4) {
              return 'TOPIK 写作必须属于任务 1-4';
            }
            return true;
          },
        }),
      },

      constraints: {
        // TOPIK II 写作固定 4 个任务
        expectedTaskCount: 4,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证任务编号范围
        const task = Number(question.task);
        if (question.task != null && (Number.isNaN(task) || task < 1 || task > 4)) {
          errors.push({
            field: 'task',
            message: 'TOPIK 写作任务编号必须在 1-4 之间',
            code: 'TOPIK_WRITING_TASK_OUT_OF_RANGE',
          });
        }

        return errors;
      },
    }
  );

/**
 * 所有 TOPIK 题目类型定义
 */
export const TOPIK_QUESTION_TYPE_DEFINITIONS: Record<
  string,
  ExamSpecificQuestionTypeDefinition
> = {
  [topikListeningDefinition.id]: topikListeningDefinition,
  [topikReadingDefinition.id]: topikReadingDefinition,
  [topikWritingDefinition.id]: topikWritingDefinition,
};

/**
 * 根据基础类型获取 TOPIK 题目类型定义
 */
export function getTOPIKQuestionTypeDefinition(
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  return Object.values(TOPIK_QUESTION_TYPE_DEFINITIONS).find(
    (def) => def.baseType === baseType
  );
}

/**
 * 获取 TOPIK 题目类型的 Section 建议
 * 根据题目类型推荐应该属于哪个 Section
 */
export function getTOPIKSectionSuggestion(
  questionTypeId: string
): 'listening' | 'reading' | 'writing' | null {
  const map: Record<string, 'listening' | 'reading' | 'writing'> = {
    [topikListeningDefinition.id]: 'listening',
    [topikReadingDefinition.id]: 'reading',
    [topikWritingDefinition.id]: 'writing',
  };
  return map[questionTypeId] ?? null;
}
